// TimelineCell.tsx
import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TimelineCellProps {
  value: any;
  config?: { color?: string };
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

export const TimelineCell: React.FC<TimelineCellProps> = ({ value, config, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);

  let startDate: Date | null = null;
  let endDate: Date | null = null;
  try {
    if (value) {
      if (typeof value === 'string') {
        const parsed = JSON.parse(value);
        const tempStart = parsed.start ? new Date(parsed.start) : null;
        const tempEnd = parsed.end ? new Date(parsed.end) : null;
        startDate = tempStart && !isNaN(tempStart.getTime()) ? tempStart : null;
        endDate = tempEnd && !isNaN(tempEnd.getTime()) ? tempEnd : null;
      } else {
        const tempStart = value.start ? new Date(value.start) : null;
        const tempEnd = value.end ? new Date(value.end) : null;
        startDate = tempStart && !isNaN(tempStart.getTime()) ? tempStart : null;
        endDate = tempEnd && !isNaN(tempEnd.getTime()) ? tempEnd : null;
      }
    }
  } catch (e) {
    // Keep null
  }

  const color = config?.color || '#0086c0';

  const handleSelectDate = (date: Date | undefined) => {
    if (!date || !onChange || readOnly) return;

    if (selectingStart) {
      onChange(JSON.stringify({ start: date.toISOString(), end: endDate?.toISOString() || date.toISOString() }));
      setSelectingStart(false);
    } else {
      onChange(JSON.stringify({ start: startDate?.toISOString() || date.toISOString(), end: date.toISOString() }));
      setIsOpen(false);
      setSelectingStart(true);
    }
  };

  const duration = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : 0;

  if (!startDate || !endDate) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            disabled={readOnly}
            className="w-full px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-50 rounded disabled:cursor-not-allowed"
          >
            -
          </button>
        </PopoverTrigger>
        {!readOnly && (
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 border-b text-sm text-gray-600">
              {selectingStart ? 'Selecione a data de início' : 'Selecione a data de término'}
            </div>
            <Calendar mode="single" selected={undefined} onSelect={handleSelectDate} initialFocus />
          </PopoverContent>
        )}
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={readOnly}
          className="w-full px-2 py-1.5 rounded hover:opacity-80 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2">
            <div className="flex-1 h-6 rounded relative overflow-hidden" style={{ backgroundColor: color + '30' }}>
              <div className="absolute inset-0 flex items-center px-2">
                <span className="text-xs font-medium" style={{ color: color }}>
                  {duration} {duration === 1 ? 'dia' : 'dias'}
                </span>
              </div>
            </div>
            <span className="text-xs text-gray-500">
              {format(startDate, 'dd/MM', { locale: ptBR })} - {format(endDate, 'dd/MM', { locale: ptBR })}
            </span>
          </div>
        </button>
      </PopoverTrigger>
      {!readOnly && (
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 border-b text-sm text-gray-600">
            {selectingStart ? 'Selecione a data de início' : 'Selecione a data de término'}
          </div>
          <Calendar
            mode="single"
            selected={selectingStart ? startDate : endDate}
            onSelect={handleSelectDate}
            initialFocus
          />
        </PopoverContent>
      )}
    </Popover>
  );
};

